import axios from 'axios';
import dayjs from 'dayjs';

import { getRequestConfig } from 'hooks/utils';
import type { ParsedSchedule } from './utils';

interface ParsedPeriod {
  period: string;
  start: string;
  end: string;
  lunch?: { wave: string; start: string; end: string }[];
}

interface ParseResponse {
  schedule_data?: {
    schedules?: { title: string; periods?: ParsedPeriod[] }[];
  };
}

const formatTime = (value: string) =>
  dayjs(value, ['hh:mm A', 'HH:mm']).format('h:mm a');

const prepareScheduleImage = async (file: File): Promise<File> => {
  // Match iOS's 2 MB image budget, below the presigned POST's 2 MiB limit.
  const maxBytes = 2_000_000;
  if (file.size <= maxBytes) return file;

  const image = await createImageBitmap(file).catch(() => {
    throw new Error('This image could not be read. Try a JPEG or PNG photo.');
  });
  try {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context)
      throw new Error(
        'Image processing is unavailable. Try a photo smaller than 2 MB.',
      );
    const scale = Math.min(1, 4000 / Math.max(image.width, image.height));
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    for (let attempt = 0; attempt < 8; attempt++) {
      context.fillStyle = '#fff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(
          resolve,
          'image/jpeg',
          Math.max(0.5, 0.9 - attempt * 0.1),
        ),
      );
      if (blob && blob.size <= maxBytes) {
        return new File([blob], `${file.name.replace(/\.[^.]+$/, '')}.jpg`, {
          type: 'image/jpeg',
        });
      }
      canvas.width = Math.max(1, Math.round(canvas.width * 0.85));
      canvas.height = Math.max(1, Math.round(canvas.height * 0.85));
    }
    throw new Error(
      'This image is too large to upload. Try a smaller photo, under 2 MB.',
    );
  } finally {
    image.close();
  }
};

export const uploadAndParseScheduleImage = async (
  file: File,
): Promise<ParsedSchedule[]> => {
  const imageFile = await prepareScheduleImage(file);
  const config = getRequestConfig();
  // Presigned fields must retain their original spelling for S3's signature.
  const { data: upload } = await axios.post<{
    image_id: string;
    url: string;
    fields: Record<string, string>;
  }>('v2/media/image-upload/initiate', null, {
    ...config,
    params: { 'file-type': imageFile.type.split('/')[1] },
  });
  const form = new FormData();
  Object.entries(upload.fields).forEach(([key, value]) =>
    form.append(key, value),
  );
  form.append('file', imageFile);
  // S3's opaque response is checked by the parser's uploaded-image validation.
  await fetch(upload.url, { method: 'POST', body: form, mode: 'no-cors' });
  const { data } = await axios.post<ParseResponse>(
    'v1/ai/schedules/from_image_ids',
    { image_ids: [upload.image_id] },
    { ...config, timeout: 120000 },
  );
  const schedules = data.schedule_data?.schedules?.map((schedule) => ({
    ...schedule,
    periods: schedule.periods ?? [],
  }));
  if (
    !schedules?.length ||
    schedules.some((schedule) => !schedule.periods.length)
  ) {
    throw new Error(
      'No schedules could be read from the image. Try a clearer photo showing period names and times.',
    );
  }
  const url = URL.createObjectURL(imageFile);
  return schedules.map((schedule, index) => ({
    genId: '',
    id: `${upload.image_id}-${index}`,
    url,
    title: schedule.title,
    periods: schedule.periods.map((period) => ({
      name: period.period,
      startTime: formatTime(period.start),
      endTime: formatTime(period.end),
    })),
    lunches: schedule.periods.flatMap((period) =>
      (period.lunch ?? []).map((lunch) => ({
        name: lunch.wave,
        startTime: formatTime(lunch.start),
        endTime: formatTime(lunch.end),
      })),
    ),
    isComplete: true,
  }));
};
